/**
 * Sounds for the interface to call directly (the HUD, panels, windows): `uiSfx.open()` when a paper
 * window opens, `uiSfx.click()` on a button, and so on. World events already sound on their own
 * (sfx/events.ts); these are for what only the interface knows about.
 */
import { sfxEngine, type SfxHandle } from './engine';
import type { SfxOpts } from './registry';

/** Plays any registered sound by id. */
export const playSfx = (id: string, o?: SfxOpts): SfxHandle | null => sfxEngine.play(id, o);

const say =
  (id: string) =>
  (o?: SfxOpts): void => {
    sfxEngine.play(id, o);
  };

let hoverAt = 0;

export const uiSfx = {
  /** A button pressed. */
  click: say('ui.click'),
  /** The pointer moved onto a button (throttled: a sweep across a row ticks a few times, not all). */
  hover: (o?: SfxOpts): void => {
    const now = typeof performance !== 'undefined' ? performance.now() : 0;
    if (now - hoverAt < 70) return;
    hoverAt = now;
    sfxEngine.play('ui.hover', o);
  },
  /** A paper window (panel, dialog, scroll) opened or closed. */
  open: say('ui.paper.open'),
  close: say('ui.paper.close'),
  /** The HUD menu opened or closed. */
  menuOpen: say('ui.menu.open'),
  menuClose: say('ui.menu.close'),
  /** Another tab chosen. */
  tab: say('ui.tab'),
  /** Not allowed, not enough, can't do that now. */
  error: say('ui.error'),
  /** A notice or toast. */
  notice: say('ui.notice'),
  /** A switch turned on or off. */
  toggle: (on: boolean, o?: SfxOpts): void => {
    sfxEngine.play(on ? 'ui.toggle.on' : 'ui.toggle.off', o);
  },
  questAccept: say('ui.quest.accept'),
  questComplete: say('ui.quest.complete'),
  levelUp: say('ui.levelUp'),
  realmUp: say('ui.realmUp'),
  pickup: say('econ.pickup'),
  coin: say('econ.coin'),
  jade: say('econ.jade'),
  equip: say('econ.equip'),
  forgeOk: say('econ.forge.ok'),
  forgeFail: say('econ.forge.fail'),
  /** The soft chime of a cultivation tick. */
  trainTick: say('ui.trainTick'),
  /** A chat message from someone else. */
  chat: say('social.chat'),
} as const;

export type UiSfx = typeof uiSfx;
